import React, {
    useEffect,
    useState,
} from "react";

import {
    Alert,
    Badge,
    Button,
    Descriptions,
    Divider,
    Drawer,
    Empty,
    List,
    Space,
    Spin,
    Tag,
    Typography,
} from "antd";

import {
    Archive,
    CalendarDays,
    CheckCircle2,
    Clock3,
    Download,
    FileArchive,
    FileText,
    History,
    ShieldAlert,
} from "lucide-react";

import api from "../api/api";


const {
    Title,
    Text,
} = Typography;


const formatFileSize = (
    bytes
) => {

    const size =
        Number(bytes) || 0;

    if (size <= 0) {
        return "0 B";
    }

    if (size < 1024) {
        return `${size} B`;
    }

    if (
        size
        < 1024 * 1024
    ) {
        return `${(
            size / 1024
        ).toFixed(1)} KB`;
    }

    if (
        size
        < 1024 * 1024 * 1024
    ) {
        return `${(
            size
            / (1024 * 1024)
        ).toFixed(1)} MB`;
    }

    return `${(
        size
        / (1024 * 1024 * 1024)
    ).toFixed(2)} GB`;
};


const getCategoryLabel = (
    category
) => {

    const labels = {
        student:
            "Student Records",
        teacher:
            "Teacher Records",
        administrative:
            "Administrative",
        academic:
            "Academic",
        financial:
            "Financial",
        hr:
            "Human Resources",
        legal:
            "Legal / Compliance",
        general:
            "General School",
    };

    return (
        labels[category]
        || category
        || "General"
    );
};


const ArchiveDocumentDetails = ({
    open,
    document,
    onClose,
    onDownload,
}) => {

    const [
        accessLogs,
        setAccessLogs,
    ] = useState([]);

    const [
        versions,
        setVersions,
    ] = useState([]);

    const [
        loadingLogs,
        setLoadingLogs,
    ] = useState(false);

    const [
        loadingVersions,
        setLoadingVersions,
    ] = useState(false);


    useEffect(() => {

        if (
            !open
            || !document?.id
        ) {
            return;
        }


        const loadAdditionalData =
            async () => {

                setLoadingLogs(
                    true
                );

                setLoadingVersions(
                    true
                );


                try {

                    const [
                        logsResponse,
                        versionsResponse,
                    ] = await Promise.all([
                        api.get(
                            `/documents/${document.id}/access-logs/`,
                            {
                                params: {
                                    page_size: 10,
                                },
                            }
                        ),

                        api.get(
                            `/documents/${document.id}/versions/`
                        ),
                    ]);


                    setAccessLogs(
                        logsResponse.data?.results
                        || []
                    );

                    setVersions(
                        versionsResponse.data?.results
                        || []
                    );

                } catch (error) {

                    console.error(
                        "Archive details error:",
                        error
                    );

                } finally {

                    setLoadingLogs(
                        false
                    );

                    setLoadingVersions(
                        false
                    );

                }

            };


        loadAdditionalData();

    }, [
        open,
        document?.id,
    ]);


    if (
        !document
    ) {
        return null;
    }


    return (

        <Drawer
            open={
                open
            }
            onClose={
                onClose
            }
            width={
                window.innerWidth < 768
                    ? "100%"
                    : 720
            }
            title={
                <Space>

                    <FileArchive
                        size={20}
                    />

                    <span>
                        Archive Document
                    </span>

                </Space>
            }
        >

            {/* =================================================
                HEADER
            ================================================= */}

            <div className="archive-detail-header">

                <div className="archive-detail-icon">

                    <FileText
                        size={30}
                    />

                </div>

                <div>

                    <Title
                        level={4}
                        style={{
                            margin:
                                0,
                        }}
                    >
                        {
                            document.title
                        }
                    </Title>

                    <Text
                        type="secondary"
                    >
                        {
                            document.reference_number
                        }
                    </Text>

                </div>

            </div>


            <Divider />


            {/* =================================================
                STATUS
            ================================================= */}

            <Space
                wrap
                style={{
                    marginBottom:
                        20,
                }}
            >

                {
                    document.is_archived
                        ? (
                            <Tag
                                color="gold"
                                icon={
                                    <Archive
                                        size={13}
                                    />
                                }
                            >
                                Archived
                            </Tag>
                        )
                        : (
                            <Tag
                                color="green"
                                icon={
                                    <CheckCircle2
                                        size={13}
                                    />
                                }
                            >
                                Active
                            </Tag>
                        )
                }


                <Tag
                    color="blue"
                >
                    {
                        getCategoryLabel(
                            document.archive_category
                        )
                    }
                </Tag>


                <Tag>
                    v{
                        document.version
                        || 1
                    }
                </Tag>


                {
                    document.is_sensitive
                        ? (
                            <Tag
                                color="volcano"
                                icon={
                                    <ShieldAlert
                                        size={13}
                                    />
                                }
                            >
                                Sensitive
                            </Tag>
                        )
                        : null
                }

            </Space>


            {/* =================================================
                ACTIONS
            ================================================= */}

            <Space
                wrap
                style={{
                    marginBottom:
                        24,
                }}
            >

                <Button
                    type="primary"
                    icon={
                        <Download
                            size={16}
                        />
                    }
                    onClick={() =>
                        onDownload(
                            document
                        )
                    }
                >
                    Download
                </Button>

            </Space>


            {/* =================================================
                DETAILS
            ================================================= */}

            <Descriptions
                bordered
                column={
                    1
                }
                size="small"
            >

                <Descriptions.Item
                    label="Reference Number"
                >
                    {
                        document.reference_number
                    }
                </Descriptions.Item>


                <Descriptions.Item
                    label="Academic Year"
                >
                    <Space>

                        <CalendarDays
                            size={15}
                        />

                        {
                            document.academic_year_details?.name
                            || "General"
                        }

                    </Space>
                </Descriptions.Item>


                <Descriptions.Item
                    label="Document Type"
                >
                    {
                        document.document_type_details?.name
                        || "—"
                    }
                </Descriptions.Item>


                <Descriptions.Item
                    label="Original Filename"
                >
                    {
                        document.original_filename
                        || "—"
                    }
                </Descriptions.Item>


                <Descriptions.Item
                    label="File Type"
                >
                    {
                        document.file_extension
                            ?.toUpperCase()
                        || "—"
                    }
                </Descriptions.Item>


                <Descriptions.Item
                    label="MIME Type"
                >
                    {
                        document.mime_type
                        || "—"
                    }
                </Descriptions.Item>


                <Descriptions.Item
                    label="File Size"
                >
                    {
                        formatFileSize(
                            document.file_size
                        )
                    }
                </Descriptions.Item>


                <Descriptions.Item
                    label="Confidentiality"
                >
                    <Tag>
                        {
                            document.confidentiality
                        }
                    </Tag>
                </Descriptions.Item>


                <Descriptions.Item
                    label="Created"
                >
                    {
                        document.created_at
                        ? new Date(
                            document.created_at
                        ).toLocaleString()
                        : "—"
                    }
                </Descriptions.Item>


                <Descriptions.Item
                    label="Last Updated"
                >
                    {
                        document.updated_at
                        ? new Date(
                            document.updated_at
                        ).toLocaleString()
                        : "—"
                    }
                </Descriptions.Item>


                <Descriptions.Item
                    label="Access Count"
                >
                    <Badge
                        count={
                            document.access_count
                            || 0
                        }
                        showZero
                    />
                </Descriptions.Item>


                <Descriptions.Item
                    label="SHA-256"
                >
                    <Text
                        code
                        copyable
                        style={{
                            wordBreak:
                                "break-all",
                        }}
                    >
                        {
                            document.file_hash
                            || "—"
                        }
                    </Text>
                </Descriptions.Item>

            </Descriptions>


            {/* =================================================
                DESCRIPTION
            ================================================= */}

            <Divider />

            <Title
                level={5}
            >
                Description
            </Title>

            <div className="archive-description-box">

                {
                    document.description
                    || "No description was provided."
                }

            </div>


            {/* =================================================
                VERSIONS
            ================================================= */}

            <Divider />

            <Title
                level={5}
            >
                <Space>

                    <History
                        size={17}
                    />

                    Version History

                </Space>
            </Title>


            {
                loadingVersions
                    ? (
                        <Spin />
                    )
                    : versions.length
                        ? (

                            <List
                                size="small"
                                dataSource={
                                    versions
                                }
                                renderItem={
                                    version => (

                                        <List.Item>

                                            <List.Item.Meta
                                                avatar={
                                                    <Tag>
                                                        v{
                                                            version.version
                                                        }
                                                    </Tag>
                                                }
                                                title={
                                                    version.title
                                                }
                                                description={
                                                    <>
                                                        {
                                                            version.original_filename
                                                        }
                                                        {" • "}
                                                        {
                                                            formatFileSize(
                                                                version.file_size
                                                            )
                                                        }
                                                    </>
                                                }
                                            />

                                            {
                                                version.is_latest_version
                                                    ? (
                                                        <Tag
                                                            color="green"
                                                        >
                                                            Latest
                                                        </Tag>
                                                    )
                                                    : null
                                            }

                                        </List.Item>

                                    )
                                }
                            />

                        )
                        : (
                            <Empty
                                image={
                                    Empty.PRESENTED_IMAGE_SIMPLE
                                }
                                description="No version history"
                            />
                        )
            }


            {/* =================================================
                ACCESS LOG
            ================================================= */}

            <Divider />

            <Title
                level={5}
            >
                <Space>

                    <Clock3
                        size={17}
                    />

                    Recent Access Activity

                </Space>
            </Title>


            {
                loadingLogs
                    ? (
                        <Spin />
                    )
                    : accessLogs.length
                        ? (

                            <List
                                size="small"
                                dataSource={
                                    accessLogs
                                }
                                renderItem={
                                    log => (

                                        <List.Item>

                                            <List.Item.Meta
                                                title={
                                                    <Space>

                                                        <Tag
                                                            color="blue"
                                                        >
                                                            {
                                                                log.action
                                                            }
                                                        </Tag>

                                                        {
                                                            log.user_identifier
                                                            || "System"
                                                        }

                                                    </Space>
                                                }
                                                description={
                                                    <>
                                                        {
                                                            log.created_at
                                                                ? new Date(
                                                                    log.created_at
                                                                ).toLocaleString()
                                                                : "—"
                                                        }

                                                        {
                                                            log.ip_address
                                                                ? ` • ${log.ip_address}`
                                                                : ""
                                                        }
                                                    </>
                                                }
                                            />

                                        </List.Item>

                                    )
                                }
                            />

                        )
                        : (
                            <Empty
                                image={
                                    Empty.PRESENTED_IMAGE_SIMPLE
                                }
                                description="No access activity"
                            />
                        )
            }

        </Drawer>

    );

};


export default ArchiveDocumentDetails;

